import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn, useMediaQuery } from "@tosize/ui";
import { type HTMLMotionProps, motion } from "motion/react";
import { useState } from "react";

interface FloatinButtonProps extends HTMLMotionProps<"button"> {
	step?: string;
	label?: string;
	icon?: IconProp;
	active?: boolean;
	size?: "default" | "small" | "extra-small";
	side?: "left" | "right";
	color?: "white" | "primary";
	alwaysShowLabel?: boolean;
}

export default function FloatingButton(props: FloatinButtonProps) {
	const {
		step,
		label,
		icon,
		active = false,
		size = "default",
		side = "left",
		color = "white",
		alwaysShowLabel = false,
		className,
		...remainingProps
	} = props;

	const isPointerDevice = useMediaQuery({ pointer: "no-touch" });
	const [isHovered, setIsHovered] = useState(false);

	return (
		<motion.button
			className={cn(
				"group inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full shadow outline-none disabled:cursor-not-allowed disabled:bg-shades-light",
				{
					"flex-row-reverse": side === "right",
					"bg-shades-medium-light": color === "primary",
					"bg-white": color === "white",
				},
				className,
			)}
			onPointerOver={() => isPointerDevice && setIsHovered(true)}
			onPointerOut={() => isPointerDevice && setIsHovered(false)}
			type="button"
			aria-label={remainingProps["aria-label"] ?? label}
			{...remainingProps}
		>
			{!!label && (
				<motion.div
					layout
					animate={isHovered || alwaysShowLabel ? "open" : "closed"}
					initial={"closed"}
					variants={{
						open: {
							visibility: "visible",
							opacity: 1,
							width: "auto",
						},
						closed: {
							visibility: "hidden",
							opacity: 0,
							width: "0px",
						},
					}}
					transition={{ type: "ease", duration: 0.2 }}
				>
					<span className="mr-2 ml-3 whitespace-nowrap">{label}</span>
				</motion.div>
			)}

			<div
				className={cn("relative p-1 group-disabled:bg-shades-light", {
					"bg-shades-medium-light": color === "primary",
					"bg-white": color === "white",
				})}
			>
				<div
					className={cn(
						"flex shrink-0 items-center justify-center rounded-full border border-transparent group-focus-visible:border-dark group-disabled:bg-shades-light motion-safe:transition-all",
						{
							"h-[34px] w-[34px] text-lg": size === "default",
							"h-[28px] w-[28px] text-base": size === "small",
							"h-[20px] w-[20px] text-mdsm": size === "extra-small",
						},

						{
							"bg-shades-light": isHovered && !active && color === "white",
							"bg-white": !(isHovered || active) && color === "white",

							"bg-shades": isHovered && !active && color === "primary",
							"bg-shades-medium-light": !(isHovered || active) && color === "primary",

							"bg-primary-dark text-white": isHovered && active,
							"bg-primary-medium text-white": !isHovered && active,
						},
					)}
				>
					{!!step && <span className="font-bold">{step}</span>}
					{icon ? <FontAwesomeIcon icon={icon} fixedWidth /> : null}
				</div>
			</div>
		</motion.button>
	);
}
